import { useState, useEffect, useCallback, useRef, useMemo } from "react";

// ═══════════════════════════════════════════════════════════════
// EVENT ENGINE (client-side store matching backend model)
// ═══════════════════════════════════════════════════════════════
const EVENT_TYPES = {
  BypassAttempt: { label: "Bypass Attempt", color: "#ef4444", icon: "⚡" },
  ContentBlocked: { label: "Content Blocked", color: "#f59e0b", icon: "🛡" },
  SafeSearchEnforced: { label: "SafeSearch Enforced", color: "#6366f1", icon: "🔍" },
  PolicyChanged: { label: "Policy Changed", color: "#8b5cf6", icon: "⚙" },
  AICoachIntervention: { label: "AI Coach", color: "#22c55e", icon: "🧠" },
  DeviceRegistered: { label: "Device Registered", color: "#06b6d4", icon: "📱" },
  DeviceRevoked: { label: "Device Revoked", color: "#64748b", icon: "🚫" },
};

const POLICY_MODULES = {
  SafeSearch: { label: "SafeSearch", color: "#6366f1" },
  TextFilter: { label: "Text Filter", color: "#f59e0b" },
  BypassDetection: { label: "Bypass Detection", color: "#ef4444" },
  AICoach: { label: "AI Coach", color: "#22c55e" },
  DomainBlock: { label: "Domain Block", color: "#f97316" },
  VPNDetection: { label: "VPN Detection", color: "#ec4899" },
  DNSFilter: { label: "DNS Filter", color: "#06b6d4" },
  KernelLock: { label: "Kernel Lock", color: "#8b5cf6" },
};

const ACTIONS = { blocked: "#ef4444", enforced: "#6366f1", warned: "#f59e0b", escalated: "#ef4444", registered: "#22c55e", revoked: "#64748b", changed: "#8b5cf6" };
const DEVICES = [
  { id: "dev_macbook", name: "MacBook Pro", os: "macOS" },
  { id: "dev_win_pc", name: "Windows PC", os: "Windows" },
  { id: "dev_iphone", name: "iPhone 15", os: "iOS" },
  { id: "dev_pixel", name: "Pixel 8", os: "Android" },
];
const TONES = ["calmFriend", "coachMode", "strictMentor"];
const LANGS = ["en", "de", "bg", "ru", "es", "fr", "tr", "ar"];

// Seed event generator
function generateEvents(count) {
  const events = [];
  const now = Date.now();
  const types = Object.keys(EVENT_TYPES);
  const policies = Object.keys(POLICY_MODULES);
  const actions = ["blocked", "enforced", "warned", "escalated"];
  const domains = ["explicit-site.com", "nsfw-redirect.xyz", "adult-mirror.net", "proxy-bypass.io", "vpn-tunnel.org", "blocked-domain.to", "search.engine.com", "social-media.com"];

  for (let i = 0; i < count; i++) {
    const type = types[Math.floor(Math.random() * types.length)];
    const device = DEVICES[Math.floor(Math.random() * DEVICES.length)];
    const ts = now - Math.floor(Math.random() * 7 * 86400000);
    const policy = type === "AICoachIntervention" ? "AICoach"
      : type === "SafeSearchEnforced" ? "SafeSearch"
      : type === "BypassAttempt" ? (Math.random() > 0.5 ? "BypassDetection" : "VPNDetection")
      : type === "DeviceRegistered" || type === "DeviceRevoked" ? "KernelLock"
      : type === "PolicyChanged" ? policies[Math.floor(Math.random() * policies.length)]
      : policies[Math.floor(Math.random() * 5)];

    const action = type === "DeviceRegistered" ? "registered"
      : type === "DeviceRevoked" ? "revoked"
      : type === "PolicyChanged" ? "changed"
      : type === "AICoachIntervention" ? (Math.random() > 0.7 ? "escalated" : "warned")
      : actions[Math.floor(Math.random() * 3)];

    events.push({
      id: `evt_${(ts).toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
      type,
      deviceId: device.id,
      deviceName: device.name,
      policyModule: policy,
      action,
      domainHash: type === "ContentBlocked" || type === "BypassAttempt" ? domains[Math.floor(Math.random() * domains.length)] : null,
      aiTone: type === "AICoachIntervention" ? TONES[Math.floor(Math.random() * TONES.length)] : null,
      langDetected: type === "BypassAttempt" ? LANGS[Math.floor(Math.random() * LANGS.length)] : null,
      severity: type === "ContentBlocked" || type === "BypassAttempt" ? Math.floor(Math.random() * 3) + 1 : null,
      timestamp: new Date(ts).toISOString(),
      ts,
    });
  }
  return events.sort((a, b) => b.ts - a.ts);
}

// ═══════════════════════════════════════════════════════════════
// ICONS
// ═══════════════════════════════════════════════════════════════
const IC = {
  Search: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  Filter: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>,
  X: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  ChevL: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>,
  ChevR: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>,
  Refresh: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/></svg>,
  Shield: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  Zap: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  Brain: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="5"/><path d="M5 18a7 7 0 0114 0"/></svg>,
  Crosshair: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="22" y1="12" x2="18" y2="12"/><line x1="6" y1="12" x2="2" y2="12"/><line x1="12" y1="6" x2="12" y2="2"/><line x1="12" y1="22" x2="12" y2="18"/></svg>,
  Clock: (p) => <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
};

// ═══════════════════════════════════════════════════════════════
// HELPER
// ═══════════════════════════════════════════════════════════════
function timeAgo(ts) {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function formatDate(ts) {
  return new Date(ts).toLocaleString("en-GB", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
}

function severityLabel(s) { return s === 3 ? "Critical" : s === 2 ? "High" : s === 1 ? "Low" : "—"; }
function severityColor(s) { return s === 3 ? "#ef4444" : s === 2 ? "#f59e0b" : "#22c55e"; }

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
export default function ActivityLogs() {
  const [allEvents, setAllEvents] = useState(() => generateEvents(200));
  const [search, setSearch] = useState("");
  const [filterTypes, setFilterTypes] = useState([]);
  const [filterDevice, setFilterDevice] = useState(null);
  const [filterPolicy, setFilterPolicy] = useState([]);
  const [filterDateRange, setFilterDateRange] = useState("7d");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [analyticsWindow, setAnalyticsWindow] = useState("24h");
  const [live, setLive] = useState(true);
  const [vis, setVis] = useState(false);
  const perPage = 20;

  useEffect(() => { requestAnimationFrame(() => setVis(true)); }, []);

  // Live event simulation
  useEffect(() => {
    if (!live) return;
    const iv = setInterval(() => {
      const newEvt = generateEvents(1)[0];
      newEvt.ts = Date.now();
      newEvt.timestamp = new Date().toISOString();
      setAllEvents(prev => [newEvt, ...prev].slice(0, 5000));
    }, 4000 + Math.random() * 6000);
    return () => clearInterval(iv);
  }, [live]);

  // Filtered + paginated
  const filtered = useMemo(() => {
    let result = [...allEvents];
    const now = Date.now();
    const ranges = { "1h": 3600000, "24h": 86400000, "7d": 604800000, "30d": 2592000000 };
    if (filterDateRange && ranges[filterDateRange]) {
      result = result.filter(e => e.ts >= now - ranges[filterDateRange]);
    }
    if (filterTypes.length > 0) result = result.filter(e => filterTypes.includes(e.type));
    if (filterDevice) result = result.filter(e => e.deviceId === filterDevice);
    if (filterPolicy.length > 0) result = result.filter(e => filterPolicy.includes(e.policyModule));
    if (search.trim()) {
      const kw = search.toLowerCase();
      result = result.filter(e =>
        e.type.toLowerCase().includes(kw) || e.policyModule.toLowerCase().includes(kw) ||
        e.action.toLowerCase().includes(kw) || e.deviceName.toLowerCase().includes(kw) ||
        (e.langDetected && e.langDetected.includes(kw)) || (e.domainHash && e.domainHash.includes(kw))
      );
    }
    return result;
  }, [allEvents, filterTypes, filterDevice, filterPolicy, filterDateRange, search]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const pageEvents = filtered.slice((page - 1) * perPage, page * perPage);

  // Analytics
  const analytics = useMemo(() => {
    const windows = { "24h": 86400000, "7d": 604800000, "30d": 2592000000 };
    const cutoff = Date.now() - (windows[analyticsWindow] || 86400000);
    const recent = allEvents.filter(e => e.ts >= cutoff);
    const totalBlocks = recent.filter(e => e.action === "blocked" || e.action === "enforced").length;
    const bypasses = recent.filter(e => e.type === "BypassAttempt").length;
    const aiCoach = recent.filter(e => e.type === "AICoachIntervention").length;
    const policyCounts = {};
    for (const e of recent) policyCounts[e.policyModule] = (policyCounts[e.policyModule] || 0) + 1;
    const top = Object.entries(policyCounts).sort((a, b) => b[1] - a[1])[0];
    // Hourly distribution for sparkline
    const hourly = new Array(24).fill(0);
    for (const e of recent.filter(r => r.ts >= Date.now() - 86400000)) {
      hourly[new Date(e.ts).getHours()]++;
    }
    return { totalBlocks, bypasses, aiCoach, topPolicy: top, hourly, total: recent.length };
  }, [allEvents, analyticsWindow]);

  const activeFilterCount = filterTypes.length + (filterDevice ? 1 : 0) + filterPolicy.length;

  const toggleType = (t) => { setFilterTypes(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t]); setPage(1); };
  const togglePolicy = (p) => { setFilterPolicy(pr => pr.includes(p) ? pr.filter(x => x !== p) : [...pr, p]); setPage(1); };
  const clearFilters = () => { setFilterTypes([]); setFilterDevice(null); setFilterPolicy([]); setSearch(""); setPage(1); };

  return (
    <div style={{ minHeight: "100vh", background: "#07070d", color: "#c8c8dc", fontFamily: "'IBM Plex Mono', 'Fira Code', monospace" }}>
      <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@300;400;500;600;700&family=IBM+Plex+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <style>{`
        *{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{width:5px;height:5px}
        ::-webkit-scrollbar-track{background:transparent}
        ::-webkit-scrollbar-thumb{background:#1c1c30;border-radius:3px}
        ::placeholder{color:#2e2e48}
        input:focus{outline:none;border-color:#6366f1 !important}
        @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        @keyframes slideIn{from{opacity:0;transform:translateX(-8px)}to{opacity:1;transform:translateX(0)}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}
        @keyframes glow{0%,100%{box-shadow:0 0 4px #22c55e30}50%{box-shadow:0 0 12px #22c55e50}}
      `}</style>

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "32px 24px 80px" }}>
        {/* ═══ HEADER ═══ */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28, opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(12px)", transition: "all 0.5s cubic-bezier(.16,1,.3,1)" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <div style={{ padding: "4px 10px", borderRadius: 5, background: "#6366f110", border: "1px solid #6366f120", color: "#818cf8", fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", display: "flex", alignItems: "center", gap: 5 }}>
                <IC.Shield style={{ width: 12, height: 12 }} />ABSOLUTEGUARD
              </div>
              <span style={{ fontSize: 10, color: "#2e2e48" }}>EVENT STREAM</span>
            </div>
            <h1 style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 28, fontWeight: 800, color: "#eeeef4", letterSpacing: "-0.03em" }}>Activity Logs</h1>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* Live indicator */}
            <button onClick={() => setLive(!live)} style={{
              display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 8,
              border: `1px solid ${live ? "#22c55e25" : "#1c1c30"}`,
              background: live ? "#22c55e08" : "transparent",
              color: live ? "#22c55e" : "#4a4a68", cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: 600,
            }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: live ? "#22c55e" : "#4a4a68", animation: live ? "glow 2s infinite" : "none" }} />
              {live ? "LIVE" : "PAUSED"}
            </button>
            <button onClick={() => setAllEvents(generateEvents(200))} style={{
              display: "flex", alignItems: "center", gap: 5, padding: "7px 12px", borderRadius: 8,
              border: "1px solid #1c1c30", background: "transparent", color: "#4a4a68", cursor: "pointer", fontFamily: "inherit", fontSize: 11,
            }}>
              <IC.Refresh style={{ width: 13, height: 13 }} />Reset
            </button>
          </div>
        </div>

        {/* ═══ ANALYTICS CARDS ═══ */}
        <div style={{ marginBottom: 24, opacity: vis ? 1 : 0, transition: "all 0.5s ease 0.1s" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
            <span style={{ fontSize: 10, color: "#3a3a58", fontWeight: 600, letterSpacing: "0.1em" }}>ANALYTICS</span>
            <div style={{ display: "flex", gap: 3 }}>
              {["24h", "7d", "30d"].map(w => (
                <button key={w} onClick={() => setAnalyticsWindow(w)} style={{
                  padding: "2px 8px", borderRadius: 4, border: "none", cursor: "pointer", fontFamily: "inherit",
                  fontSize: 10, fontWeight: 600,
                  background: analyticsWindow === w ? "#6366f118" : "transparent",
                  color: analyticsWindow === w ? "#818cf8" : "#3a3a58",
                }}>{w}</button>
              ))}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
            <AnalyticsCard icon={<IC.Shield style={{ width: 16, height: 16 }} />} label="Total Blocks" value={analytics.totalBlocks} color="#f59e0b" sparkline={analytics.hourly} />
            <AnalyticsCard icon={<IC.Zap style={{ width: 16, height: 16 }} />} label="Bypass Attempts" value={analytics.bypasses} color="#ef4444" />
            <AnalyticsCard icon={<IC.Brain style={{ width: 16, height: 16 }} />} label="AI Interventions" value={analytics.aiCoach} color="#22c55e" />
            <AnalyticsCard icon={<IC.Crosshair style={{ width: 16, height: 16 }} />} label="Top Policy" value={analytics.topPolicy ? analytics.topPolicy[0] : "—"} sub={analytics.topPolicy ? `${analytics.topPolicy[1]} events` : ""} color="#6366f1" text />
          </div>
        </div>

        {/* ═══ SEARCH + FILTERS BAR ═══ */}
        <div style={{ display: "flex", gap: 8, marginBottom: 12, opacity: vis ? 1 : 0, transition: "all 0.5s ease 0.15s" }}>
          {/* Search */}
          <div style={{ flex: 1, position: "relative" }}>
            <IC.Search style={{ width: 14, height: 14, position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#3a3a58" }} />
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search events… (type, device, policy, language)"
              style={{ width: "100%", padding: "10px 12px 10px 34px", borderRadius: 8, border: "1px solid #1c1c30", background: "#0b0b14", color: "#c8c8dc", fontSize: 12, fontFamily: "inherit" }} />
            {search && <button onClick={() => setSearch("")} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#4a4a68", padding: 0, display: "flex" }}><IC.X style={{ width: 14, height: 14 }} /></button>}
          </div>
          {/* Filter toggle */}
          <button onClick={() => setShowFilters(!showFilters)} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "10px 14px", borderRadius: 8,
            border: `1px solid ${showFilters || activeFilterCount ? "#6366f130" : "#1c1c30"}`,
            background: showFilters || activeFilterCount ? "#6366f108" : "transparent",
            color: showFilters || activeFilterCount ? "#818cf8" : "#4a4a68",
            cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 500,
          }}>
            <IC.Filter style={{ width: 14, height: 14 }} />
            Filters{activeFilterCount > 0 && <span style={{ padding: "1px 6px", borderRadius: 10, background: "#6366f130", fontSize: 10, fontWeight: 700 }}>{activeFilterCount}</span>}
          </button>
          {/* Date range */}
          <div style={{ display: "flex", gap: 2 }}>
            {["1h", "24h", "7d", "30d"].map(r => (
              <button key={r} onClick={() => { setFilterDateRange(r); setPage(1); }} style={{
                padding: "10px 12px", borderRadius: 8, border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: 600,
                background: filterDateRange === r ? "#1c1c30" : "transparent",
                color: filterDateRange === r ? "#c8c8dc" : "#3a3a58",
              }}>{r}</button>
            ))}
          </div>
        </div>

        {/* ═══ FILTER PANEL ═══ */}
        {showFilters && (
          <div style={{ padding: "16px 20px", borderRadius: 10, background: "#0b0b14", border: "1px solid #1c1c30", marginBottom: 16, animation: "fadeUp 0.2s ease" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <span style={{ fontSize: 10, color: "#3a3a58", fontWeight: 700, letterSpacing: "0.1em" }}>FILTER BY</span>
              {activeFilterCount > 0 && <button onClick={clearFilters} style={{ fontSize: 11, color: "#ef4444", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>Clear all</button>}
            </div>
            {/* Event types */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: "#3a3a58", fontWeight: 600, marginBottom: 6, letterSpacing: "0.06em" }}>EVENT TYPE</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {Object.entries(EVENT_TYPES).map(([key, val]) => {
                  const active = filterTypes.includes(key);
                  return (
                    <button key={key} onClick={() => toggleType(key)} style={{
                      padding: "5px 10px", borderRadius: 5, border: `1px solid ${active ? val.color + "40" : "#1c1c30"}`,
                      background: active ? val.color + "12" : "transparent", color: active ? val.color : "#4a4a68",
                      cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: 500,
                    }}>{val.icon} {val.label}</button>
                  );
                })}
              </div>
            </div>
            {/* Policy modules */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: "#3a3a58", fontWeight: 600, marginBottom: 6, letterSpacing: "0.06em" }}>POLICY MODULE</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {Object.entries(POLICY_MODULES).map(([key, val]) => {
                  const active = filterPolicy.includes(key);
                  return (
                    <button key={key} onClick={() => togglePolicy(key)} style={{
                      padding: "5px 10px", borderRadius: 5, border: `1px solid ${active ? val.color + "40" : "#1c1c30"}`,
                      background: active ? val.color + "12" : "transparent", color: active ? val.color : "#4a4a68",
                      cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: 500,
                    }}>{val.label}</button>
                  );
                })}
              </div>
            </div>
            {/* Devices */}
            <div>
              <div style={{ fontSize: 10, color: "#3a3a58", fontWeight: 600, marginBottom: 6, letterSpacing: "0.06em" }}>DEVICE</div>
              <div style={{ display: "flex", gap: 4 }}>
                <button onClick={() => { setFilterDevice(null); setPage(1); }} style={{
                  padding: "5px 10px", borderRadius: 5, border: `1px solid ${!filterDevice ? "#6366f130" : "#1c1c30"}`,
                  background: !filterDevice ? "#6366f110" : "transparent", color: !filterDevice ? "#818cf8" : "#4a4a68",
                  cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: 500,
                }}>All</button>
                {DEVICES.map(d => {
                  const active = filterDevice === d.id;
                  return (
                    <button key={d.id} onClick={() => { setFilterDevice(active ? null : d.id); setPage(1); }} style={{
                      padding: "5px 10px", borderRadius: 5, border: `1px solid ${active ? "#06b6d440" : "#1c1c30"}`,
                      background: active ? "#06b6d410" : "transparent", color: active ? "#06b6d4" : "#4a4a68",
                      cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: 500,
                    }}>{d.name}</button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ═══ RESULTS HEADER ═══ */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, padding: "0 4px" }}>
          <span style={{ fontSize: 11, color: "#3a3a58" }}>{filtered.length} events{search && ` matching "${search}"`}</span>
          <span style={{ fontSize: 11, color: "#3a3a58" }}>Page {page} of {Math.max(1, totalPages)}</span>
        </div>

        {/* ═══ EVENT TIMELINE ═══ */}
        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 20 }}>
          {pageEvents.map((evt, i) => {
            const et = EVENT_TYPES[evt.type];
            const isNew = Date.now() - evt.ts < 10000;
            return (
              <div key={evt.id} onClick={() => setSelected(selected?.id === evt.id ? null : evt)}
                style={{
                  display: "grid", gridTemplateColumns: "32px 1fr 120px 100px 80px 70px",
                  alignItems: "center", padding: "10px 14px", borderRadius: 8, gap: 12,
                  background: selected?.id === evt.id ? "#12121f" : isNew ? "#0f1018" : "#0a0a12",
                  border: `1px solid ${selected?.id === evt.id ? "#6366f125" : isNew ? "#22c55e15" : "#12121e"}`,
                  cursor: "pointer", transition: "all 0.15s",
                  animation: isNew ? "slideIn 0.3s ease" : undefined,
                }}
                onMouseEnter={e => { if (selected?.id !== evt.id) e.currentTarget.style.background = "#0e0e18"; }}
                onMouseLeave={e => { if (selected?.id !== evt.id) e.currentTarget.style.background = isNew ? "#0f1018" : "#0a0a12"; }}>
                {/* Type icon */}
                <div style={{
                  width: 32, height: 32, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center",
                  background: et.color + "10", border: `1px solid ${et.color}20`, fontSize: 14,
                }}>{et.icon}</div>
                {/* Main info */}
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: et.color }}>{et.label}</span>
                    <span style={{ padding: "1px 6px", borderRadius: 3, fontSize: 9, fontWeight: 600, background: ACTIONS[evt.action] + "15", color: ACTIONS[evt.action], textTransform: "uppercase", letterSpacing: "0.06em" }}>{evt.action}</span>
                    {evt.severity && <span style={{ padding: "1px 6px", borderRadius: 3, fontSize: 9, fontWeight: 600, background: severityColor(evt.severity) + "15", color: severityColor(evt.severity) }}>SEV {evt.severity}</span>}
                    {isNew && <span style={{ padding: "1px 5px", borderRadius: 3, fontSize: 8, fontWeight: 700, background: "#22c55e20", color: "#22c55e", animation: "pulse 1.5s infinite" }}>NEW</span>}
                  </div>
                  <div style={{ fontSize: 10, color: "#3a3a58", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {evt.domainHash && <span style={{ color: "#5a5a78" }}>{evt.domainHash} · </span>}
                    {POLICY_MODULES[evt.policyModule]?.label || evt.policyModule}
                    {evt.langDetected && <span> · lang:{evt.langDetected}</span>}
                    {evt.aiTone && <span> · tone:{evt.aiTone}</span>}
                  </div>
                </div>
                {/* Device */}
                <div style={{ fontSize: 11, color: "#4a4a68", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{evt.deviceName}</div>
                {/* Policy */}
                <div style={{ padding: "3px 8px", borderRadius: 4, background: (POLICY_MODULES[evt.policyModule]?.color || "#6366f1") + "10", color: POLICY_MODULES[evt.policyModule]?.color || "#6366f1", fontSize: 10, fontWeight: 600, textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {POLICY_MODULES[evt.policyModule]?.label || evt.policyModule}
                </div>
                {/* Time */}
                <div style={{ fontSize: 10, color: "#3a3a58", textAlign: "right", whiteSpace: "nowrap" }}>{timeAgo(evt.ts)}</div>
                {/* Date */}
                <div style={{ fontSize: 10, color: "#2e2e48", textAlign: "right", whiteSpace: "nowrap" }}>{formatDate(evt.ts)}</div>
              </div>
            );
          })}

          {pageEvents.length === 0 && (
            <div style={{ padding: 40, textAlign: "center", color: "#3a3a58", fontSize: 13 }}>
              No events match your filters.
            </div>
          )}
        </div>

        {/* ═══ EXPANDED EVENT DETAIL ═══ */}
        {selected && (
          <div style={{
            position: "fixed", bottom: 0, left: 0, right: 0,
            background: "#0d0d18", borderTop: "1px solid #1c1c30",
            padding: "20px 32px", zIndex: 100,
            animation: "fadeUp 0.2s ease",
            boxShadow: "0 -8px 40px rgba(0,0,0,0.5)",
          }}>
            <div style={{ maxWidth: 1280, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 20 }}>
              <DetailField label="Event ID" value={selected.id} mono />
              <DetailField label="Type" value={EVENT_TYPES[selected.type]?.label} color={EVENT_TYPES[selected.type]?.color} />
              <DetailField label="Timestamp" value={new Date(selected.ts).toISOString()} mono />
              <DetailField label="Device" value={`${selected.deviceName} (${selected.deviceId})`} />
              <DetailField label="Policy" value={POLICY_MODULES[selected.policyModule]?.label || selected.policyModule} color={POLICY_MODULES[selected.policyModule]?.color} />
              <DetailField label="Action" value={selected.action} color={ACTIONS[selected.action]} />
              {selected.severity && <DetailField label="Severity" value={`${selected.severity} — ${severityLabel(selected.severity)}`} color={severityColor(selected.severity)} />}
              {selected.aiTone && <DetailField label="AI Tone" value={selected.aiTone} color="#22c55e" />}
              {selected.langDetected && <DetailField label="Language Detected" value={selected.langDetected.toUpperCase()} color="#06b6d4" />}
              {selected.domainHash && <DetailField label="Domain (hashed)" value={selected.domainHash} mono />}
              <div style={{ display: "flex", alignItems: "flex-end" }}>
                <button onClick={() => setSelected(null)} style={{
                  padding: "6px 14px", borderRadius: 6, border: "1px solid #1c1c30",
                  background: "transparent", color: "#4a4a68", cursor: "pointer", fontFamily: "inherit", fontSize: 11,
                }}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ PAGINATION ═══ */}
        {totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8 }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={{
              padding: "8px", borderRadius: 6, border: "1px solid #1c1c30", background: "transparent",
              color: page <= 1 ? "#1c1c30" : "#4a4a68", cursor: page <= 1 ? "default" : "pointer", display: "flex",
            }}><IC.ChevL style={{ width: 16, height: 16 }} /></button>
            {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
              let pg;
              if (totalPages <= 7) pg = i + 1;
              else if (page <= 4) pg = i + 1;
              else if (page >= totalPages - 3) pg = totalPages - 6 + i;
              else pg = page - 3 + i;
              return (
                <button key={pg} onClick={() => setPage(pg)} style={{
                  width: 32, height: 32, borderRadius: 6, border: "none", cursor: "pointer", fontFamily: "inherit",
                  fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center",
                  background: page === pg ? "#6366f120" : "transparent",
                  color: page === pg ? "#818cf8" : "#3a3a58",
                }}>{pg}</button>
              );
            })}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={{
              padding: "8px", borderRadius: 6, border: "1px solid #1c1c30", background: "transparent",
              color: page >= totalPages ? "#1c1c30" : "#4a4a68", cursor: page >= totalPages ? "default" : "pointer", display: "flex",
            }}><IC.ChevR style={{ width: 16, height: 16 }} /></button>
          </div>
        )}

        {/* ═══ PRIVACY FOOTER ═══ */}
        <div style={{ textAlign: "center", marginTop: 40, fontSize: 10, color: "#1e1e34", letterSpacing: "0.06em" }}>
          🔒 No explicit content stored · Domains hashed · GDPR compliant · Metadata only
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════
function AnalyticsCard({ icon, label, value, color, sub, sparkline, text }) {
  return (
    <div style={{
      padding: "16px 18px", borderRadius: 10, background: "#0b0b14",
      border: "1px solid #14141e", position: "relative", overflow: "hidden",
    }}>
      <div style={{ position: "absolute", top: -10, right: -10, width: 60, height: 60, borderRadius: "50%", background: color, opacity: 0.04, filter: "blur(20px)" }} />
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <div style={{ color: color + "90" }}>{icon}</div>
        <span style={{ fontSize: 10, color: "#3a3a58", fontWeight: 600, letterSpacing: "0.06em" }}>{label}</span>
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
        <div style={{
          fontSize: text ? 14 : 28, fontWeight: 800, color,
          fontFamily: text ? "'IBM Plex Mono', monospace" : "'IBM Plex Sans', sans-serif",
          letterSpacing: text ? "0" : "-0.03em",
        }}>
          {value}
        </div>
        {/* Mini sparkline */}
        {sparkline && (
          <div style={{ display: "flex", alignItems: "flex-end", gap: 1, height: 24 }}>
            {sparkline.map((v, i) => {
              const max = Math.max(...sparkline, 1);
              return <div key={i} style={{ width: 3, borderRadius: 1, height: Math.max(2, (v / max) * 24), background: color, opacity: 0.5 }} />;
            })}
          </div>
        )}
      </div>
      {sub && <div style={{ fontSize: 10, color: "#3a3a58", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function DetailField({ label, value, color, mono }) {
  return (
    <div>
      <div style={{ fontSize: 9, color: "#3a3a58", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 4, textTransform: "uppercase" }}>{label}</div>
      <div style={{
        fontSize: 12, color: color || "#c8c8dc", fontWeight: 500,
        fontFamily: mono ? "'IBM Plex Mono', monospace" : "inherit",
        wordBreak: "break-all",
      }}>{value || "—"}</div>
    </div>
  );
}